import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useIntl, FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';

import { MessageBannerVariants } from '../definitions/MessageBannerDefinitions';
import MAGIC_LINK_VARIANTS from '../definitions/MagicLinkVariants';
import MagicLinkSignInMessagesPropType from '../definitions/MagicLinkSignInMessagesPropType';
import { requestMagicLink } from '../functions/requestMagicLink';
import getDashboardBasePath from '../functions/getDashboardBasePath';
import useIsMounted from '../hooks/useIsMounted';
import trackHeapEvent from '../functions/trackHeapEvent';
import useSessionStore from '../hooks/useSessionStore';
import MessageBanner from './MessageBanner';
import MagicLinkSignInLegacy from './MagicLinkSignInLegacy';
import MagicLinkConfirmationScreenLegacy from './MagicLinkConfirmationScreenLegacy';
import CircleSpinner from './CircleSpinner';
import ModalContainer from './ModalContainer';


const MAGIC_LINK_SIGN_IN_SCREEN = 'SignIn';
const MAGIC_LINK_CONFIRMATION_SCREEN = 'Confirmation';

function MagicLinkFormsLegacy({
    supportPhone,
    messages = {},
    hideLoginDescription,
    onRequestLinkCustom,
    emailForForcedLogin,
    resumeApplicationLink,
    documentOptions = {},
    magicLinkVariant,
    isOpen,
    onClose,
    shouldShowGoogleSignIn,
    ...otherProps
}) {
    const sessionStore = useSessionStore();
    const intl = useIntl();
    const [ activeScreen, setActiveScreen ] = useState(MAGIC_LINK_SIGN_IN_SCREEN);

    const {
        email,
        dispatchMagicLinkRequest,
        isRequestingMagicLink,
        magicLinkError,
    } = useMagicLink({
        emailForForcedLogin,
        resumeApplicationLink,
        magicLinkVariant,
        documentOptions,
        onRequestLinkCustom,
    });

    useEffect(
        () => {
            if (emailForForcedLogin) {
                handleMagicLinkSignIn(emailForForcedLogin)
                    .catch(() => {}); // error handled in the hook
            }
        },
        // handleMagicLinkSignIn does not need to be in the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ emailForForcedLogin ],
    );

    async function handleMagicLinkSignIn(signInEmail) {
        if (await dispatchMagicLinkRequest(signInEmail)) {
            if (shouldShowGoogleSignIn) {
                trackHeapEvent('[GoogleSSOExperiment] MagicLink requested', {
                    loginSource: 'MagicLink',
                });
            }

            setActiveScreen(MAGIC_LINK_CONFIRMATION_SCREEN);
        }
    }

    async function handleGoogleSignIn(providerToken) {
        const { document, documentType, documentRedirect } = documentOptions;

        // same with authorize page
        const resumeLink = resumeApplicationLink
            ?? documentRedirect
            ?? `${getDashboardBasePath(intl.locale)}`;

        await sessionStore.signInBySocialService({
            providerToken,
            redirectTo: resumeLink,
            document,
            documentType,
        });
    }

    function handleClose() {
        setActiveScreen(MAGIC_LINK_SIGN_IN_SCREEN);
        onClose?.();
    }

    return (
        <ModalContainer
            isOpen={isOpen}
            onClose={handleClose}
            showBranding={true}
            contentMaxWidth="712px"
            {...otherProps}
        >
            <Container className="rh-mx-auto rh-py-4">
                <If condition={magicLinkError}>
                    <MessageBanner
                        iconKey="exclamation-mark"
                        variant={MessageBannerVariants.ERROR}
                        message={(
                            <FormattedMessage
                                id="Accounts.LegacyMagicLinkForms.ErrorMessage"
                                defaultMessage={`{activeScreen, select,
                                    SignIn {There has been a problem signing you in. Please try again later.}
                                    Confirmation {There has been a problem resending the email. Please try again later.}
                                    other {}
                                }`}
                                values={{ activeScreen }}
                            />
                        )}
                        data-name="magicLinkForm-errorBanner"
                    />
                </If>

                <If condition={isRequestingMagicLink}>
                    <CircleSpinner
                        position="fixed"
                        callerName="MagicLinkForms"
                    />
                </If>

                <Choose>
                    <When condition={activeScreen === MAGIC_LINK_SIGN_IN_SCREEN}>
                        <MagicLinkSignInLegacy
                            email={email}
                            onSubmit={handleMagicLinkSignIn}
                            messages={messages[MAGIC_LINK_SIGN_IN_SCREEN]}
                            hideDescription={hideLoginDescription}
                            shouldShowGoogleSignIn={shouldShowGoogleSignIn}
                            onGoogleSignIn={handleGoogleSignIn}
                        />
                    </When>

                    <When condition={activeScreen === MAGIC_LINK_CONFIRMATION_SCREEN}>
                        <MagicLinkConfirmationScreenLegacy
                            email={email}
                            supportPhone={supportPhone}
                            messages={messages[MAGIC_LINK_CONFIRMATION_SCREEN]}
                            className="confirmation"
                        />
                    </When>
                </Choose>
            </Container>
        </ModalContainer>
    );
}

MagicLinkFormsLegacy.propTypes = {
    shouldShowGoogleSignIn: PropTypes.bool,
    supportPhone: PropTypes.string,
    messages: PropTypes.shape({
        [MAGIC_LINK_SIGN_IN_SCREEN]: MagicLinkSignInMessagesPropType,
        [MAGIC_LINK_CONFIRMATION_SCREEN]: PropTypes.object,
    }),
    hideLoginDescription: PropTypes.bool,
    onRequestLinkCustom: PropTypes.func,
    emailForForcedLogin: PropTypes.string,
    magicLinkVariant: PropTypes.oneOf(Object.values(MAGIC_LINK_VARIANTS)),
    resumeApplicationLink: PropTypes.string,
    documentOptions: PropTypes.shape({
        document: PropTypes.object,
        documentType: PropTypes.string,
        documentToken: PropTypes.string,
        documentRedirect: PropTypes.string,
    }),
    onSetActiveScreen: PropTypes.func, // Optional callback to notify parent component of screen changes
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
};

function useMagicLink({
    emailForForcedLogin,
    magicLinkVariant,
    resumeApplicationLink,
    documentOptions,
    onRequestLinkCustom,
}) {
    const getIsMounted = useIsMounted();
    const [ email, setEmail ] = useState(emailForForcedLogin);
    const [ isRequestingMagicLink, setRequestingMagicLink ] = useState(false);
    const [ magicLinkError, setMagicLinkError ] = useState(null);

    const queryParams = {};
    if (resumeApplicationLink != null) {
        queryParams.redirect = resumeApplicationLink;
    }

    async function dispatchMagicLinkRequest(requestedEmail) {
        let requestError = null; // setState is async

        if (!getIsMounted()) {
            return false;
        }

        setRequestingMagicLink(true);
        setMagicLinkError(null);
        setEmail(requestedEmail);

        try {
            onRequestLinkCustom
                ? await onRequestLinkCustom(requestedEmail)
                : await requestMagicLink({
                    email: requestedEmail,
                    queryParams,
                    documentOptions,
                    variant: magicLinkVariant,
                });
        } catch (error) {
            requestError = error;

            if (!getIsMounted()) {
                return false;
            }

            setMagicLinkError(error);

            // eslint-disable-next-line no-console
            console.error(`[useMagicLink]: error requesting magic link: ${error.message}`, error);
        }

        if (!getIsMounted()) {
            return false;
        }

        setRequestingMagicLink(false);

        return requestError == null;
    }

    return {
        // state
        email,
        isRequestingMagicLink,
        magicLinkError,
        // methods
        dispatchMagicLinkRequest,
    };
}

const Container = styled.div`
    max-width: 600px;
`;

export default observer(MagicLinkFormsLegacy);

export { MAGIC_LINK_SIGN_IN_SCREEN, MAGIC_LINK_CONFIRMATION_SCREEN };
